import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

// ── Workspace Invite Acceptance — /workspace-invite/{token} ──────────────────
// Handles both existing users (just accept) and new users (sign up first)

const FONT = "'Inter',Arial,sans-serif";
const P    = '#2B1DC9';

const ROLE_INFO = {
  admin:   { label:'Admin',   color:'#DC2626', desc:'Full access to all Business tools' },
  manager: { label:'Manager', color:'#D97706', desc:'CRM, Clients, Invoices, Proposals' },
  sales:   { label:'Sales',   color:'#7C3AED', desc:'CRM, Clients, and Proposals' },
  caller:  { label:'Caller',  color:'#2563EB', desc:'CRM and Leads only' },
  viewer:  { label:'Viewer',  color:'#6B7280', desc:'Read-only access' },
};

export default function WorkspaceInvitePage() {
  const token = window.location.pathname.split('/workspace-invite/')[1];
  const [invite,  setInvite]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [user,    setUser]    = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted,  setAccepted]  = useState(false);

  useEffect(() => {
    (async () => {
      // Check current session
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) setUser(session.user);

      // Load invite by token
      if (!token) { setError('Invalid invite link.'); setLoading(false); return; }
      const { data, error: err } = await supabase
        .from('workspace_members')
        .select('*, workspaces(name, owner_id)')
        .eq('invite_token', token)
        .maybeSingle();

      if (err || !data) {
        setError('This invite link is invalid. Please ask the workspace owner to send a new invitation.');
        setLoading(false); return;
      }
      if (data.status === 'active') {
        // Already accepted — just redirect to app
        setAccepted(true); setInvite(data); setLoading(false); return;
      }
      // Check 24hr expiry
      if (data.expires_at && new Date(data.expires_at) < new Date()) {
        setError('This invite link has expired (links are valid for 24 hours). Please ask the workspace owner to send a new invitation.');
        setLoading(false); return;
      }

      // Fetch owner profile
      const { data: owner } = await supabase
        .from('profiles')
        .select('full_name, biz_name, email')
        .eq('id', data.owner_id)
        .maybeSingle();

      setInvite({ ...data, _owner: owner });
      setLoading(false);
    })();
  }, [token]);

  const acceptInvite = async () => {
    if (!user) {
      // Redirect to login, come back after
      sessionStorage.setItem('pending_workspace_invite', token);
      window.location.href = '/';
      return;
    }
    setAccepting(true);
    try {
      // Link the member_id to this user and activate
      await supabase
        .from('workspace_members')
        .update({
          member_id:   user.id,
          status:      'active',
          joined_at:   new Date().toISOString(),
          data_owner_id: invite.owner_id,
        })
        .eq('invite_token', token);

      setAccepted(true);
    } catch(e) {
      setError('Failed to accept invite: ' + e.message);
    }
    setAccepting(false);
  };

  const ri = invite ? (ROLE_INFO[invite.role] || ROLE_INFO.viewer) : null;
  const ownerName = invite?._owner?.biz_name || invite?._owner?.full_name || 'Someone';

  const card = {
    background: '#fff', borderRadius:16, padding:36,
    maxWidth:480, width:'100%', margin:'0 auto',
    boxShadow:'0 8px 40px rgba(43,29,201,.12)',
  };

  if (loading) return (
    <div style={{minHeight:'100vh',background:'#F6F6F8',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:FONT}}>
      <div style={{textAlign:'center',color:'#8888AA'}}>Loading invite…</div>
    </div>
  );

  if (error) return (
    <div style={{minHeight:'100vh',background:'#F6F6F8',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:FONT,padding:24}}>
      <div style={{...card,textAlign:'center'}}>
        <div style={{fontSize:48,marginBottom:12}}>🔗</div>
        <div style={{fontSize:18,fontWeight:700,color:'#0F0E1A',marginBottom:8}}>Invite Unavailable</div>
        <div style={{color:'#4A4A6A',fontSize:14,marginBottom:24}}>{error}</div>
        <a href="/" style={{color:P,fontWeight:700,textDecoration:'none'}}>← Go to Aspreo</a>
      </div>
    </div>
  );

  if (accepted) return (
    <div style={{minHeight:'100vh',background:'#F6F6F8',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:FONT,padding:24}}>
      <div style={{...card,textAlign:'center'}}>
        <div style={{width:72,height:72,borderRadius:'50%',background:'#F0FDF4',border:'2px solid #16A34A',display:'flex',alignItems:'center',justifyContent:'center',fontSize:36,margin:'0 auto 20px'}}>✅</div>
        <div style={{fontSize:22,fontWeight:800,color:'#0F0E1A',marginBottom:8}}>You're in!</div>
        <div style={{color:'#4A4A6A',fontSize:14,lineHeight:1.7,marginBottom:24}}>
          You've joined <strong>{invite?.workspaces?.name}</strong> as <strong style={{color:ri?.color}}>{ri?.label}</strong>.<br/>
          Open Aspreo and go to Business to start working.
        </div>
        <a href="/" style={{display:'inline-block',background:P,color:'#fff',borderRadius:10,padding:'12px 28px',fontWeight:700,fontSize:14,textDecoration:'none'}}>
          Open Aspreo →
        </a>
      </div>
    </div>
  );

  return (
    <div style={{minHeight:'100vh',background:'#F6F6F8',fontFamily:FONT,display:'flex',alignItems:'center',justifyContent:'center',padding:24}}>
      <div style={card}>
        {/* Header */}
        <div style={{background:`linear-gradient(135deg,${P},#4F46E5)`,borderRadius:12,padding:'24px 28px',marginBottom:28,color:'#fff',textAlign:'center'}}>
          <div style={{fontSize:11,background:'rgba(255,255,255,.2)',borderRadius:99,padding:'4px 14px',display:'inline-block',fontWeight:700,marginBottom:10}}>🏢 Team Invitation</div>
          <div style={{fontSize:20,fontWeight:800,marginBottom:4}}>{ownerName}</div>
          <div style={{fontSize:13,opacity:.8}}>invited you to join their workspace</div>
        </div>

        {/* Workspace info */}
        <div style={{background:'#F6F6F8',borderRadius:10,padding:'14px 18px',marginBottom:20}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
            <div style={{fontSize:13,color:'#8888AA'}}>Workspace</div>
            <div style={{fontWeight:700,fontSize:14,color:'#0F0E1A'}}>{invite?.workspaces?.name}</div>
          </div>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
            <div style={{fontSize:13,color:'#8888AA'}}>Your Role</div>
            <span style={{fontSize:12,padding:'3px 10px',borderRadius:99,background:ri?.color+'18',color:ri?.color,fontWeight:700}}>{ri?.label}</span>
          </div>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div style={{fontSize:13,color:'#8888AA'}}>Access</div>
            <div style={{fontSize:12,color:'#4A4A6A',textAlign:'right'}}>{ri?.desc}</div>
          </div>
        </div>

        {/* What they'll see */}
        <div style={{marginBottom:24}}>
          <div style={{fontSize:12,fontWeight:700,color:'#8888AA',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:10}}>What you'll have access to</div>
          <div style={{fontSize:13,color:'#4A4A6A',lineHeight:1.7,background:'#EFF6FF',border:'1px solid #BFDBFE',borderRadius:8,padding:'10px 14px'}}>
            {ri?.desc} — within <strong>{ownerName}'s</strong> business workspace.<br/>
            <strong>No plan purchase needed.</strong> Your access is granted by the workspace owner.
          </div>
        </div>

        {!user ? (
          <div>
            <div style={{background:'#FFFBEB',border:'1px solid #D97706',borderRadius:8,padding:'10px 14px',fontSize:13,color:'#92400E',marginBottom:16}}>
              ⚠️ You need to be logged in to Aspreo to accept this invite.
            </div>
            <button onClick={acceptInvite} style={{width:'100%',padding:'13px',background:P,color:'#fff',border:'none',borderRadius:10,fontFamily:FONT,fontSize:15,fontWeight:700,cursor:'pointer',marginBottom:8}}>
              Sign In / Sign Up to Accept →
            </button>
            <div style={{fontSize:12,color:'#8888AA',textAlign:'center'}}>
              If you don't have an Aspreo account, create one free (takes 30 seconds).<br/>You won't need to upgrade your plan.
            </div>
          </div>
        ) : (
          <div>
            <div style={{background:'#F0FDF4',border:'1px solid #16A34A',borderRadius:8,padding:'10px 14px',fontSize:13,color:'#14532D',marginBottom:16}}>
              ✅ Signed in as <strong>{user.email}</strong>
            </div>
            <button onClick={acceptInvite} disabled={accepting}
              style={{width:'100%',padding:'13px',background:P,color:'#fff',border:'none',borderRadius:10,fontFamily:FONT,fontSize:15,fontWeight:700,cursor:'pointer',opacity:accepting?0.6:1}}>
              {accepting ? '⏳ Accepting…' : '✅ Accept Invitation'}
            </button>
          </div>
        )}

        <div style={{fontSize:11,color:'#8888AA',textAlign:'center',marginTop:16}}>
          Powered by <strong style={{color:P}}>Aspreo</strong> · <a href="https://www.aspreo.com" style={{color:P}}>aspreo.com</a>
        </div>
      </div>
    </div>
  );
}
